import { DOCUMENT, Location } from '@angular/common';
import { effect, Inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, ParamMap, Params, Router, UrlSerializer } from '@angular/router';
import { IViolationsViewModel } from 'src/app/models/viewModels/violationsViewModel';
import { IInventoryResourceViewModel } from 'src/app/models/viewModels/inventoryResourceViewModel';
import { AiAgentStepActivityTableRowViewModel } from 'src/app/models/viewModels/aiAgentStepActivityTableRowViewModel';
import { AiFindingsTableRowViewModel } from 'src/app/models/viewModels/aiFindingsTableRowViewModel';
import { AgentStepModel } from 'src/app/models/api/agentStep.model';
import { IssuesTableRowViewModel } from 'src/app/models/viewModels/issuesTableRowViewModel';
import { IDataLayerTableRowViewModel } from 'src/app/models/viewModels/dataLayerTableRowViewModel';
import { NavigationParams, QueryParams } from '../../../models/navigation/navigationParams.interface';
import { SidePanelComponent } from '../components/side-panel.component';
import { SidePanelQueryParamsState } from '../side-panel-query-params-state';
import { SidePanelViewModel } from '../types/side-panel';
import { PanelLayer } from '../types/panel-layer';
import { AiPreventionMockTableRowViewModel } from 'src/app/models/viewModels/aiPreventionMockTableRowViewModel';
import { Subscription } from 'rxjs';
import { InLineControlTableRowViewModel } from 'src/app/models/viewModels/inLineControlTableRowViewModel';
import { InLineActivityStepTableRowViewModel } from 'src/app/models/viewModels/InLineActivityStepTableRowView.model';
import {
  SIDEPANEL_MIN_WIDTH,
  SIDEPANEL_MAX_WIDTH,
  SIDEPANEL_MAX_VW,
  SIDEPANEL_STORAGE_KEY,
  SIDEPANEL_STACKED_OFFSET,
} from 'src/app/models/constants/sidepanel.constants';
import { createStackEntry, createStackParamKey, isStackParam } from '../side-panel-config';
import { IssueComponent } from 'src/app/views/issue/issue.component';
import { EnterpriseAiFindingComponent } from 'src/app/views/enterprise-ai/enterprise-ai-finding/enterprise-ai-finding.component';
import { AgentStepComponent } from 'src/app/views/enterprise-ai/enterprise-ai-agent-step/enterprise-ai-agent-step.component';
import { PreventionDetailsComponent } from 'src/app/views/enterprise-ai/enterprise-ai-prevention-details/enterprise-ai-prevention-details.component';
import { EnterpriseAiInLineControlDetailsComponent } from 'src/app/views/enterprise-ai/enterprise-ai-in-line-control-details/enterprise-ai-in-line-control-details.component';
import { DataLayerDetailsComponent } from 'src/app/views/data-layer/data-layer-details/data-layer-details.component';
import { ViolationRuleDetailsComponent } from 'src/app/views/violation-rule-details/violation-rule-details.component';
import { EnterpriseAiFindingThreadComponent } from 'src/app/views/enterprise-ai/enterprise-ai-finding-thread/enterprise-ai-finding-thread.component';
import { SessionDrawerComponent } from 'src/app/components/attack-simulations/session-drawer/session-drawer.component';
import { BoundaryReviewComponent } from 'src/app/components/boundary-review/boundary-review.component';
import { IRedTeamSession } from 'src/app/models/api/red-team/red-team-session.model';

/**
 * Controls stacking behavior:
 * - `true`: Force stacking on top of existing panel
 * - `false`: Force opening as main panel (replaces existing)
 * - `undefined`: Auto-detect based on current panel state (default)
 *
 * Auto-detection logic:
 * - If a panel is already open AND the new panel type is stackable → stack
 * - Otherwise → open as main panel
 */
export interface OpenPanelOptions {
  stack?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SidePanelService {
  private _sidePanel: SidePanelComponent;
  private readonly _state = new SidePanelQueryParamsState();

  private activeRouteSubscription$: Subscription | undefined;

  // Track when a navigation was initiated by this service to ignore spurious empty param emissions
  private _lastNavigationTime = 0;

  private readonly _panelWidthState = signal(this._loadWidthFromStorage());

  public readonly panelWidth = this._panelWidthState.asReadonly();
  // Track when panels were manually closed to prevent re-opening from stale router state
  private _lastCloseTime = 0;

  constructor(
    private readonly _router: Router,
    private readonly _activeRoute: ActivatedRoute,
    private readonly _location: Location,
    private readonly _urlSerializer: UrlSerializer,
    @Inject(DOCUMENT) private readonly _document: Document
  ) {
    effect(() => {
      const width = this._panelWidthState();
      this._document.documentElement.style.setProperty('--sidepanel-width', `${width}px`);
      this._document.documentElement.style.setProperty('--sidepanel-offset', `${-width}px`);
      this._document.documentElement.style.setProperty(
        '--sidepanel-stacked-offset',
        `${-(width + SIDEPANEL_STACKED_OFFSET)}px`
      );
    });
  }

  public setWidth(width: number): void {
    const clampedWidth = this._clampWidth(width);
    this._panelWidthState.set(clampedWidth);
    this._saveWidthToStorage(clampedWidth);
  }

  private _loadWidthFromStorage(): number {
    const stored = localStorage.getItem(SIDEPANEL_STORAGE_KEY);

    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed)) {
        return this._clampWidth(parsed);
      }
    }

    return SIDEPANEL_MIN_WIDTH;
  }

  private _saveWidthToStorage(width: number): void {
    localStorage.setItem(SIDEPANEL_STORAGE_KEY, String(width));
  }

  private _clampWidth(width: number): number {
    const maxVwPx = (window.innerWidth * SIDEPANEL_MAX_VW) / 100;
    const effectiveMax = Math.min(SIDEPANEL_MAX_WIDTH, maxVwPx);
    return Math.max(SIDEPANEL_MIN_WIDTH, Math.min(effectiveMax, width));
  }

  public set panel(panel: SidePanelComponent) {
    this._sidePanel = panel;
    if (this.activeRouteSubscription$) {
      this.activeRouteSubscription$.unsubscribe();
    }

    this.activeRouteSubscription$ = this._activeRoute.queryParamMap.subscribe((params) => {
      // Ignore spurious empty param emissions that occur right after we initiated a navigation
      // This happens due to Angular router internals when navigating from child routes
      const timeSinceNavigation = Date.now() - this._lastNavigationTime;
      if (params.keys.length === 0 && timeSinceNavigation < 100) {
        return;
      }

      this._updateSidePanelStateV2(params);
    });
  }

  public isInsideSidePanel(target: HTMLElement): boolean {
    return this._sidePanel.isInside(target);
  }

  public close() {
    if (this._sidePanel.layers().length === 0 && !this._sidePanel.isOpen) {
      return;
    }
    this._closeAllPanels();
  }

  public closePanel(stackKeys: string[]) {
    this._closePanel(stackKeys);
  }

  public openAgentStepDetailsFromFinding(threadId: string, agentStepId: string, agentStep?: AgentStepModel) {
    const queryParams: Params = {
      stepThreadId: threadId,
      agentStep: agentStepId,
    };

    if (agentStep?.integration?.id) {
      queryParams['integration'] = agentStep.integration.id;
    }

    this._openAsNested(queryParams, agentStep);
  }

  public openFindingDetailsFromIssue(finding: AiFindingsTableRowViewModel) {
    this._openAsNested(
      {
        finding: finding.id,
        integration: finding.integrationId,
      },
      finding
    );
  }

  public openViolationDetailsFromIssue(
    violationId: string,
    integrationId: string,
    violationView?: IViolationsViewModel
  ) {
    this._openAsNested(
      {
        violation: violationId,
        integration: integrationId,
      },
      violationView
    );
  }

  public openResourceDetailsFromIssue(resourceId: string, integrationId: string) {
    this._openAsNested({
      resource: resourceId,
      integration: integrationId,
    });
  }

  public openInLineControlDetails(
    resource: InLineControlTableRowViewModel,
    opts?: OpenPanelOptions & { inline?: boolean }
  ) {
    const params: Params = {
      integration: resource.integrationId,
      inLineControl: resource.id,
    };
    if (opts?.inline) {
      params['inline'] = 'true';
    }
    if (this._shouldUseStacking(opts)) {
      this._openAsNested(params, resource);
    } else {
      this._openAsMain(params, resource);
    }
  }

  public openViolationDetails(
    violationId: string,
    integrationId: string,
    violationView?: IViolationsViewModel,
    opts?: OpenPanelOptions
  ) {
    if (this._shouldUseStacking(opts)) {
      this.navigateStacked({
        violation: violationId,
        integration: integrationId,
      });
    } else {
      this._openAsMain(
        {
          integration: integrationId,
          violation: violationId,
          resource: null,
        },
        violationView
      );
    }
  }

  public openViolationRuleDetails(
    violationRuleId: string,
    integrationId: string,
    policyId: string,
    viewModel?: SidePanelViewModel,
    ruleType?: 'detection' | 'violation',
    opts?: OpenPanelOptions
  ) {
    const params = {
      violationRule: violationRuleId,
      integration: integrationId,
      policy: policyId,
      ruleType: ruleType ?? null,
    };
    if (this._shouldUseStacking(opts)) {
      this._openAsNested(params, viewModel);
    } else {
      this._openAsMain(params, viewModel);
    }
  }

  public openResourceDetails(resource: IInventoryResourceViewModel, opts?: OpenPanelOptions) {
    if (this._shouldUseStacking(opts)) {
      this.navigateStacked({
        resource: resource.resource.id,
        integration: resource.resource.integration.id,
      });
    } else {
      this._openAsMain(
        {
          integration: resource.resource.integration.id,
          resource: resource.resource.id,
          violation: null,
        },
        resource
      );
    }
  }

  public openAgentStepDetails(resource: AiAgentStepActivityTableRowViewModel, opts?: OpenPanelOptions) {
    const params = {
      stepThreadId: resource.threadId,
      integration: resource.integrationId,
      agentStep: resource.id,
    };
    if (this._shouldUseStacking(opts)) {
      this._openAsNested(params, resource);
    } else {
      this._openAsMain(params, resource);
    }
  }

  public openInLineStepDetails(
    resource: InLineActivityStepTableRowViewModel,
    opts?: OpenPanelOptions & { inline?: boolean }
  ) {
    const params: Params = {
      stepThreadId: (resource as unknown as { threadId?: string }).threadId,
      integration: resource.integrationId,
      agentStep: resource.id,
    };
    if (opts?.inline) {
      params['inline'] = 'true';
    }
    if (this._shouldUseStacking(opts)) {
      this._openAsNested(params, resource);
    } else {
      this._openAsMain(params, resource);
    }
  }

  public openFindingDetails(finding: AiFindingsTableRowViewModel, opts?: OpenPanelOptions) {
    const params = { integration: finding.integrationId, finding: finding.id };
    if (this._shouldUseStacking(opts)) {
      this._openAsNested(params, finding);
    } else {
      this._openAsMain(params, finding);
    }
  }

  public openPreventionDetails(preventionView: AiPreventionMockTableRowViewModel, opts?: OpenPanelOptions) {
    const params = {
      preventionId: preventionView.id,
      integration: preventionView.integrationId,
    };
    if (this._shouldUseStacking(opts)) {
      this._openAsNested(params, preventionView);
    } else {
      this._openAsMain(params, preventionView);
    }
  }

  public openBoundaryReview(inputs: {
    issueId: string;
    issueName: string;
    issueSeverity: string;
    issueResourceIcon: string;
  }) {
    const owner = this._getOwnerElement();
    const currentLayers = this._sidePanel.layers();
    const newLayer: PanelLayer = {
      component: BoundaryReviewComponent,
      inputBindings: inputs,
      stackKey: 'boundaryReview',
    };
    this._sidePanel.openStacked(owner, [...currentLayers, newLayer]);
  }

  public openRedTeamSessionDetails(session: IRedTeamSession, opts?: OpenPanelOptions) {
    const params = { redTeamSession: session.sessionId };
    if (this._shouldUseStacking(opts)) {
      this._openAsNested(params, session);
    } else {
      this._openAsMain(params, session);
    }
  }

  public openIssueDetails(issueView: IssuesTableRowViewModel, opts?: OpenPanelOptions) {
    const issue = issueView.issue;
    const params = { integration: issue.integrationId, issue: issue.id };
    if (this._shouldUseStacking(opts)) {
      this._openAsNested(params, issueView);
    } else {
      this._openAsMain(params, issueView);
    }
  }

  public openFindingThread(findingThreadId: string, opts?: OpenPanelOptions, sourcePipeline?: string) {
    const params: Record<string, string> = { findingThreadId };
    if (sourcePipeline) {
      params['sourcePipeline'] = sourcePipeline;
    }
    if (this._shouldUseStacking(opts)) {
      this._openAsNested(params);
    } else {
      this._openAsMain(params);
    }
  }

  public openFindingThreadNested(findingThreadId: string, sourcePipeline?: string) {
    const queryParams: Params = { findingThreadId };
    if (sourcePipeline) {
      queryParams['sourcePipeline'] = sourcePipeline;
    }
    this._openAsNested(queryParams);
  }

  public openDataLensDetails(item: IDataLayerTableRowViewModel, opts?: OpenPanelOptions) {
    // TODO: Using 'system' as a placeholder integration ID for data lens items
    // since they may come from different sources
    const params = {
      integration: 'system',
      dataItem: item.id,
    };
    if (this._shouldUseStacking(opts)) {
      this._openAsNested(params, item);
    } else {
      this._openAsMain(params, item);
    }
  }

  private _updateSidePanelStateV2(params: ParamMap) {
    // If panels were recently closed manually, ignore this update
    // This prevents re-opening from stale params or the navigation we triggered to clear params
    const timeSinceClose = Date.now() - this._lastCloseTime;

    if (timeSinceClose < 300) {
      return;
    }

    // Standard path - update state with new params
    this._state.update(this._router, params);

    const owner = this._getOwnerElement();
    const currentLayers = this._sidePanel.layers();

    // Build params object for non-stackable panel check
    const paramsObj: Params = {};
    for (const key of params.keys) {
      paramsObj[key] = params.get(key);
    }
    const viewModel = this._router.getCurrentNavigation()?.extras?.state?.['viewModel'];

    // Get stackable panels (resource, violation)
    const sidePanels = this._state.getSidePanelsToOpen();

    // Get non-stackable panel (issue, finding, agentStep, etc.)
    const nonStackableLayer = this._buildNonStackablePanelLayer(paramsObj, viewModel);

    if (sidePanels.length) {
      // Check if we're replacing the topmost panel with same component type
      if (currentLayers.length > 0 && sidePanels.length <= currentLayers.length) {
        const topmostCurrentComponent = currentLayers[currentLayers.length - 1].component;
        const newTopmostPanel = sidePanels[sidePanels.length - 1];

        if (newTopmostPanel.component === topmostCurrentComponent) {
          const newTopmostLayer: PanelLayer = {
            component: newTopmostPanel.component,
            inputBindings: newTopmostPanel.inputs,
            keyboardShortcuts: newTopmostPanel.shortcuts,
            stackKey: newTopmostPanel.stackKey,
          };

          const parentLayers = currentLayers.slice(0, sidePanels.length - 1);
          this._sidePanel.openStacked(owner, [...parentLayers, newTopmostLayer]);
          return;
        }
      }

      const stackableLayers: PanelLayer[] = sidePanels.map(({ component, inputs, shortcuts, stackKey }) => ({
        component,
        inputBindings: inputs,
        keyboardShortcuts: shortcuts,
        stackKey,
      }));

      // Combine non-stackable layer (if exists) with stackable layers
      // Non-stackable comes first as the base, stackable layers on top
      const allLayers = nonStackableLayer ? [nonStackableLayer, ...stackableLayers] : stackableLayers;

      this._sidePanel.openStacked(owner, allLayers);
      return;
    }

    // Only non-stackable panel exists
    if (nonStackableLayer) {
      this._sidePanel.openStacked(owner, [nonStackableLayer]);
      return;
    }

    // No panels to open, close any existing ones
    this._sidePanel.closeAllStackedPanels();
  }

  private _getOwnerElement(): HTMLElement {
    return (
      (this._document.querySelector('table') as HTMLElement) ||
      (this._document.querySelector('app-table table') as HTMLElement) ||
      (this._document.querySelector('app-table') as HTMLElement) ||
      (this._document.body as HTMLElement)
    );
  }

  private _openAsMain(queryParams: Params, viewModel?: SidePanelViewModel) {
    this._lastNavigationTime = Date.now();
    const mergedParams = { ...this._state.getEmptyRouteState(), ...queryParams };

    // Use Location.replaceState to update URL without triggering Angular router navigation.
    // This avoids guards re-running and navigation conflicts that cause "Navigation ID X
    // is not equal to current navigation id Y" errors.
    this._updateUrlWithoutNavigation(mergedParams);

    // Manually update state and open panel since we're not using router navigation
    this._state.updateWithViewModel(this._createParamMap(mergedParams), viewModel);

    const owner = this._getOwnerElement();

    // First try stackable panels (resource, violation)
    const sidePanels = this._state.getSidePanelsToOpen();

    if (sidePanels.length) {
      const layers: PanelLayer[] = sidePanels.map(({ component, inputs, shortcuts, stackKey }) => ({
        component,
        inputBindings: inputs,
        keyboardShortcuts: shortcuts,
        stackKey,
      }));

      this._sidePanel.openStacked(owner, layers);
      return;
    }

    // Try non-stackable panels (issue, finding, agentStep, etc.)
    const nonStackableLayer = this._buildNonStackablePanelLayer(mergedParams, viewModel);
    if (nonStackableLayer) {
      this._sidePanel.openStacked(owner, [nonStackableLayer]);
    }
  }

  /**
   * Updates the browser URL without triggering Angular Router navigation.
   * Uses Location.replaceState() which updates the address bar but doesn't
   * trigger route guards, resolvers, or queryParamMap subscriptions.
   *
   * Important: This method properly handles stack params (s0, s1, etc.) by
   * removing any stack params from the current URL that aren't explicitly
   * included in the new params. This prevents stale stack params from persisting.
   */
  private _updateUrlWithoutNavigation(params: Params): void {
    const currentUrlTree = this._router.parseUrl(this._router.url);

    const mergedParams: { [key: string]: string } = {};
    for (const [key, value] of Object.entries({ ...currentUrlTree.queryParams, ...params })) {
      // Skip null/undefined values (these are used to clear params)
      if (value === null || value === undefined) {
        continue;
      }
      // Skip stack params (s0, s1, etc.) from current URL unless they're explicitly
      // included in the new params. This ensures stack params are properly cleared
      // when closing panels, even if getEmptyRouteState() doesn't know about them
      // (e.g., after _stackEntries was cleared by a previous panel close).
      if (isStackParam(key) && !(key in params)) {
        continue;
      }
      mergedParams[key] = value;
    }

    currentUrlTree.queryParams = mergedParams;

    const newUrl = this._urlSerializer.serialize(currentUrlTree);
    this._location.replaceState(newUrl);
  }

  private _createParamMap(params: Params): ParamMap {
    return {
      has: (key: string) => params[key] !== null && params[key] !== undefined,
      get: (key: string) => params[key] ?? null,
      getAll: (key: string) => (params[key] ? [params[key]] : []),
      keys: Object.keys(params).filter((key) => params[key] !== null && params[key] !== undefined),
    };
  }

  private _openAsNested(queryParams: Params, viewModel?: SidePanelViewModel) {
    this._lastNavigationTime = Date.now();

    // Get current state and merge with new params
    const currentState = this._state.getRouteState();
    const mergedParams = { ...currentState };
    for (const key of Object.keys(queryParams) as QueryParams[]) {
      mergedParams[key] = queryParams[key];
    }

    // Use Location.replaceState to update URL without triggering Angular router navigation.
    this._updateUrlWithoutNavigation(mergedParams);

    // Manually update state and open panel since we're not using router navigation
    this._state.updateWithViewModel(this._createParamMap(mergedParams), viewModel);

    const owner = this._getOwnerElement();

    // Get current layers to preserve non-stackable panels (issue, finding, etc.)
    const currentLayers = this._sidePanel.layers();
    const nonStackableCurrentLayers = currentLayers.filter(
      (layer) => !layer.inputBindings['resource'] && !layer.inputBindings['violation']
    );

    // First try stackable panels (resource, violation)
    const sidePanels = this._state.getSidePanelsToOpen();

    if (sidePanels.length) {
      // Convert sidePanels to layers - always use the parsed panels from URL state
      // This ensures consistency when transitioning between single and stacked formats
      const stackableLayers: PanelLayer[] = sidePanels.map(({ component, inputs, shortcuts, stackKey }) => ({
        component,
        inputBindings: inputs,
        keyboardShortcuts: shortcuts,
        stackKey,
      }));

      // Preserve non-stackable layers and add new stackable layers on top
      this._sidePanel.openStacked(owner, [...nonStackableCurrentLayers, ...stackableLayers]);
      return;
    }

    // Try non-stackable panels (issue, finding, agentStep, etc.)
    // For nested non-stackable panels, we keep existing layers and add the new one
    const nonStackableLayer = this._buildNonStackablePanelLayer(mergedParams, viewModel);
    if (nonStackableLayer) {
      this._sidePanel.openStacked(owner, [...currentLayers, nonStackableLayer]);
    }
  }

  /**
   * Navigate to add a stacked panel on top of existing panels.
   * Uses explicit position params (s0, s1, s2...) to preserve order.
   *
   * URL format: ?integration=abc&s0=resource:id1&s1=violation:id2&s2=resource:id3
   */
  public navigateStacked(queryParams: NavigationParams): void {
    const currentState = this._state.getRouteState();
    const nextIndex = this._state.getNextStackIndex();
    const isCurrentlyStacking = this._state.isStackingMode();

    // Build merged params for URL
    const mergedParams: Params = { [QueryParams.integration]: currentState[QueryParams.integration] };

    // If we're already in stacking mode, keep existing stack params
    if (isCurrentlyStacking) {
      for (const [key, value] of Object.entries(currentState)) {
        if (key.startsWith('s') && /^s\d+$/.test(key) && value) {
          mergedParams[key] = value;
        }
      }
    } else {
      // Convert single panel format to stacked format (s0)
      // Only if we have a single panel open
      if (this._state.getStackSize() === 1) {
        const sidePanels = this._state.getSidePanelsToOpen();
        if (sidePanels.length === 1) {
          const panel = sidePanels[0];
          // Determine the type from the current single panel
          const panelType = panel.stackKey; // 'resource' or 'violation'
          const panelId = panel.inputs['resource'] ?? panel.inputs['violation'];
          if (panelType && panelId) {
            mergedParams['s0'] = createStackEntry(panelType, panelId as string);
            // Clear old single-panel format params
            mergedParams[QueryParams.resource] = null;
            mergedParams[QueryParams.violation] = null;
          }
        }
      }
    }

    // Add new panel(s) at next stack position
    let currentIndex = isCurrentlyStacking ? nextIndex : 1;

    if (queryParams.resource) {
      mergedParams[createStackParamKey(currentIndex)] = createStackEntry('resource', queryParams.resource);
      currentIndex++;
    }

    if (queryParams.violation) {
      mergedParams[createStackParamKey(currentIndex)] = createStackEntry('violation', queryParams.violation);
    }

    // Preserve integration if provided
    if (queryParams.integration) {
      mergedParams[QueryParams.integration] = queryParams.integration;
    }

    this._openAsNested(mergedParams);
  }

  /**
   * Build a panel layer for non-stackable panel types (issue, finding, agentStep, etc.).
   * These panels don't support stacking and are handled separately from the registry.
   *
   * PRIORITY ORDER (most nested first):
   * When multiple non-stackable params exist in URL (e.g., ?issue=X&finding=Y),
   * we prioritize the "most nested" panel - the one that would typically be opened
   * on top of others. This ensures correct behavior for direct URL navigation.
   *
   * Order: agentStep > findingThread > finding > issue > others (standalone)
   */
  private _buildNonStackablePanelLayer(params: Params, viewModel?: SidePanelViewModel): PanelLayer | null {
    const integration = params[QueryParams.integration];

    // Agent step panel (highest priority - can be nested in finding or inLineControl)
    if (params[QueryParams.agentStep]) {
      return {
        component: AgentStepComponent,
        inputBindings: {
          agentStep: params[QueryParams.agentStep],
          stepThreadId: params['stepThreadId'],
          integration,
          viewModel,
          inline: params['inline'] ?? null,
        },
        stackKey: QueryParams.agentStep,
      };
    }

    // Finding thread panel
    if (params['findingThreadId']) {
      return {
        component: EnterpriseAiFindingThreadComponent,
        inputBindings: {
          findingThreadId: params['findingThreadId'],
          ...(params['sourcePipeline'] && { sourcePipeline: params['sourcePipeline'] }),
        },
        stackKey: 'findingThreadId',
      };
    }

    // Finding panel (can be nested in issue)
    if (params[QueryParams.finding]) {
      return {
        component: EnterpriseAiFindingComponent,
        inputBindings: {
          finding: params[QueryParams.finding],
          integration,
          viewModel,
        },
        stackKey: QueryParams.finding,
      };
    }

    // Issue panel
    if (params[QueryParams.issue]) {
      return {
        component: IssueComponent,
        inputBindings: {
          issue: params[QueryParams.issue],
          integration,
          viewModel,
        },
        stackKey: QueryParams.issue,
      };
    }

    // Standalone panels (no nesting hierarchy) - alphabetical order

    // Data lens panel
    if (params[QueryParams.dataItem]) {
      return {
        component: DataLayerDetailsComponent,
        inputBindings: {
          dataItem: params[QueryParams.dataItem],
          integration,
          viewModel,
        },
        stackKey: QueryParams.dataItem,
      };
    }

    // InLine control panel
    if (params[QueryParams.inLineControl]) {
      return {
        component: EnterpriseAiInLineControlDetailsComponent,
        inputBindings: {
          inLineControl: params[QueryParams.inLineControl],
          integration,
          viewModel,
        },
        stackKey: QueryParams.inLineControl,
      };
    }

    // Prevention panel
    if (params['preventionId']) {
      return {
        component: PreventionDetailsComponent,
        inputBindings: {
          prevention: params['preventionId'],
        },
        stackKey: 'preventionId',
      };
    }

    // Violation rule panel
    if (params['violationRule']) {
      return {
        component: ViolationRuleDetailsComponent,
        inputBindings: {
          violationRule: params['violationRule'],
          integration,
          viewModel,
          policy: params['policy'] ?? null,
          ruleType: params['ruleType'] ?? null,
        },
        stackKey: 'violationRule',
      };
    }

    // Red team session panel
    if (params[QueryParams.redTeamSession]) {
      return {
        component: SessionDrawerComponent,
        inputBindings: {
          sessionId: params[QueryParams.redTeamSession],
          viewModel,
        },
        stackKey: QueryParams.redTeamSession,
      };
    }

    return null;
  }

  private _closeAllPanels(): void {
    this._lastCloseTime = Date.now();

    // Use _updateUrlWithoutNavigation to clear params consistently.
    // This avoids issues where router.navigate() uses stale internal state
    // that doesn't match the browser URL (due to previous replaceState calls).
    const emptyState = this._state.getEmptyRouteState();
    this._updateUrlWithoutNavigation(emptyState);

    this._sidePanel.closeAllStackedPanels();
    this._state.updateWithViewModel({ has: () => false, get: () => null, getAll: () => [], keys: [] }, undefined);
  }

  /**
   * Close a panel by its stack key.
   * For stacked panels, removes the param and renumbers remaining stack params.
   * For non-stackable panels, clears the appropriate URL params.
   */
  private _closePanel(stackKeys: string[]): void {
    // Mark as recently closed to prevent route subscription from overriding our changes
    this._lastCloseTime = Date.now();

    const currentLayers = this._sidePanel.layers();

    // Find the layer to close based on stack keys
    const layerToCloseIndex = currentLayers.findIndex((layer) => stackKeys.includes(layer.stackKey));

    // If we couldn't find a specific layer, close the topmost one
    const indexToClose = layerToCloseIndex >= 0 ? layerToCloseIndex : currentLayers.length - 1;

    if (indexToClose < 0) {
      return;
    }

    // Remove only the specific panel, keep all others
    const remainingLayers = currentLayers.filter((_, index) => index !== indexToClose);

    if (remainingLayers.length === 0) {
      // No panels left, close all
      this._lastCloseTime = Date.now();
      const emptyState = this._state.getEmptyRouteState();

      // Use _updateUrlWithoutNavigation to ensure consistency with how other panel
      // closures work. This avoids issues where router.navigate() uses stale internal
      // state that doesn't match the browser URL (due to previous replaceState calls).
      this._updateUrlWithoutNavigation(emptyState);

      this._sidePanel.closeAllStackedPanels();
      this._state.updateWithViewModel({ has: () => false, get: () => null, getAll: () => [], keys: [] }, undefined);
      return;
    }

    // Start with empty state to clear all panel params, then add back what we need
    const emptyState = this._state.getEmptyRouteState();
    const updatedParams: Params = { ...emptyState };

    // Rebuild params from remaining layers
    const stackableLayers = remainingLayers.filter(
      (layer) => layer.inputBindings['resource'] || layer.inputBindings['violation']
    );
    const nonStackableLayers = remainingLayers.filter(
      (layer) => !layer.inputBindings['resource'] && !layer.inputBindings['violation']
    );

    // Handle stackable layers (resource, violation)
    if (stackableLayers.length === 1) {
      const layer = stackableLayers[0];
      const resourceId = layer.inputBindings['resource'];
      const violationId = layer.inputBindings['violation'];
      updatedParams[QueryParams.integration] = layer.inputBindings['integration'] as string;

      if (resourceId) {
        updatedParams[QueryParams.resource] = resourceId as string;
      } else if (violationId) {
        updatedParams[QueryParams.violation] = violationId as string;
      }
    } else if (stackableLayers.length > 1) {
      updatedParams[QueryParams.integration] = stackableLayers[0].inputBindings['integration'] as string;
      stackableLayers.forEach((layer, index) => {
        const resourceId = layer.inputBindings['resource'];
        const violationId = layer.inputBindings['violation'];
        const panelType = resourceId ? 'resource' : 'violation';
        const panelId = (resourceId ?? violationId) as string;
        updatedParams[createStackParamKey(index)] = createStackEntry(panelType, panelId);
      });
    }

    // Handle non-stackable layers
    for (const layer of nonStackableLayers) {
      const bindings = layer.inputBindings;
      if (bindings['integration']) {
        updatedParams[QueryParams.integration] = bindings['integration'] as string;
      }
      if (bindings['issue']) {
        updatedParams[QueryParams.issue] = bindings['issue'] as string;
      }
      if (bindings['finding']) {
        updatedParams[QueryParams.finding] = bindings['finding'] as string;
      }
      if (bindings['agentStep']) {
        updatedParams[QueryParams.agentStep] = bindings['agentStep'] as string;
        if (bindings['stepThreadId']) {
          updatedParams['stepThreadId'] = bindings['stepThreadId'] as string;
        }
        if (bindings['inline']) {
          updatedParams['inline'] = bindings['inline'] as string;
        }
      }
      if (bindings['inLineControl']) {
        updatedParams[QueryParams.inLineControl] = bindings['inLineControl'] as string;
      }
      if (bindings['dataItem']) {
        updatedParams[QueryParams.dataItem] = bindings['dataItem'] as string;
      }
      if (bindings['prevention']) {
        updatedParams['preventionId'] = bindings['prevention'] as string;
      }
      if (bindings['violationRule']) {
        updatedParams['violationRule'] = bindings['violationRule'] as string;
        if (bindings['policy']) {
          updatedParams['policy'] = bindings['policy'] as string;
        }
        if (bindings['ruleType']) {
          updatedParams['ruleType'] = bindings['ruleType'] as string;
        }
      }
      if (bindings['findingThreadId']) {
        updatedParams['findingThreadId'] = bindings['findingThreadId'] as string;
        if (bindings['sourcePipeline']) {
          updatedParams['sourcePipeline'] = bindings['sourcePipeline'] as string;
        }
      }
    }

    // Update URL
    this._updateUrlWithoutNavigation(updatedParams);

    // Update state
    this._state.updateWithViewModel(this._createParamMap(updatedParams), undefined);

    // Re-open panels with remaining layers
    const owner = this._getOwnerElement();
    const layers: PanelLayer[] = remainingLayers.map((layer, index) => {
      // For stackable layers, update stackKey if in stacked mode
      if (layer.inputBindings['resource'] || layer.inputBindings['violation']) {
        return {
          ...layer,
          stackKey: stackableLayers.length === 1 ? layer.stackKey : createStackParamKey(index),
        };
      }
      return layer;
    });

    this._sidePanel.openStacked(owner, layers, true);
  }

  /**
   * Determines whether to use stacking navigation based on:
   * 1. Explicit option if provided
   * 2. Auto-detection: stack if a panel is already open
   *
   * @param opts Options that may contain explicit stack preference
   * @returns true if stacking should be used, false otherwise
   */
  private _shouldUseStacking(opts?: OpenPanelOptions): boolean {
    // Explicit opt-out
    if (opts?.stack === false) return false;

    // Explicit opt-in
    if (opts?.stack === true) return true;

    // Auto-detect: stack if any panel is currently open
    return !!this._sidePanel?.isOpen;
  }
}
